import React from 'react';
import cx from 'classnames';
import FullPageSlider from 'components/slider/full-page-slider';
import FullPageSlide from 'components/slider/full-page-slide';
import { AnimationAppear } from 'components/animations/animations';
import { Assets } from 'components/assets/assets';
import Image from 'next/legacy/image';
import { Columns, ColumnsItem } from 'components/columns';
import CtaBox, { CtaBoxAlign } from 'components/cta-box/cta-box';
import CtaBoxTitle, { CtaBoxTitleAlign } from 'components/cta-box/cta-box-title';
import CtaBoxMedia from 'components/cta-box/cta-box-media';
import { slides, slidesCommonContent } from './slides';
import PageFooter from './page-footer';
import { PageVideoBlockContainer } from 'components/page-video-block';
import useMediaQuery from 'lib/use-media-query';
import { SwiperProps } from 'swiper/swiper-react';
import { InView } from 'react-intersection-observer';

type PageSliderPropsTypes = {
    slideIndex?: number;
    showFaqLink?: boolean;
    height?: number;
    color?: string;
    goToTopHandler?: () => void;
    onInit?: SwiperProps['onInit'];
    onResize?: SwiperProps['onResize'];
    onRealIndexChange?: SwiperProps['onRealIndexChange'];
};

const sliderColumnsWidth = 996;

const PageSlider: React.FC<PageSliderPropsTypes> = ({
    slideIndex,
    height,
    showFaqLink,
    color,
    goToTopHandler,
    onInit,
    onResize,
    onRealIndexChange,
}) => {
    const slide = slideIndex || 0;
    const isBreakpointMobileDevice = useMediaQuery(1140);
    const isBreakpointsmallTabletMobileDevice = useMediaQuery(900);
    const isBreakpointTabletMobileDevice = useMediaQuery(700);

    const currentSliderVideoPosition = 0;
    // @TODO: write what this figures mean
    const desktopSliderVideoPosition = `translate3d(0, ${currentSliderVideoPosition}, 0) translate3d(-50%, -50%, 0) translateX(64px) translateX(${`${
        sliderColumnsWidth / 2
    }px`})`;
    const mobileSliderVideoPosition = `translate3d(-50%, ${currentSliderVideoPosition}, 0) translate3d(0, -80%, 0)`;

    const mobileMediaBoxPlaceholder = (
        <div
            style={{
                opacity: 0,
                visibility: 'hidden',
                width: '100%',
                paddingTop: '35vh',
                margin: '0 auto',
            }}
        />
    );
    const floatingVideo = (
        <InView>
            {({ inView, ref }) => (
                <div ref={ref}>
                    {inView ? <PageVideoBlockContainer videoIndex={slide} /> : null}
                </div>
            )}
        </InView>
    );
    const floatingVideoDW = (
        <div
            style={{
                position: 'fixed',
                top: '50%',
                right: '50%',
                marginLeft: 'auto',
                width: '100%',
                maxWidth: 312,
                transform: desktopSliderVideoPosition,
                zIndex: 1,
                transition: 'opacity 300ms, transform 400ms ease-in 50ms',
                opacity: [1, 2, 3].includes(slide) ? undefined : 0,
                visibility: [1, 2, 3].includes(slide) ? undefined : 'hidden',
            }}
        >
            {floatingVideo}
        </div>
    );
    const floatingVideoMW = (
        <div
            className="page-slider__video"
            style={{
                maxWidth: isBreakpointTabletMobileDevice ? '43vw' : '32vw',
                // todo - check on mobile devices better version
                transform: mobileSliderVideoPosition,
                zIndex: 1,
                transition: 'opacity 200ms, transform 400ms',
                opacity: [1, 2, 3].includes(slide) ? 1 : 0,
                visibility: [1, 2, 3].includes(slide) ? undefined : 'hidden',
            }}
        >
            {floatingVideo}
        </div>
    );

    const className = cx(
        {
            'page-slider': true,
        },
        typeof slide !== 'undefined' && `page-slider--slide-${slide}`,
    );

    return (
        <>
            <FullPageSlider
                updateOnWindowResize={true}
                shortSwipes={false}
                cssMode={true}
                direction="vertical"
                autoHeight={true}
                height={height}
                pagination={{ clickable: true }}
                onInit={onInit}
                onResize={onResize}
                onRealIndexChange={onRealIndexChange}
                className={className}
            >
                <FullPageSlide extraClass="night-in">
                    {isBreakpointMobileDevice ? (
                        slidesCommonContent.one
                    ) : (
                        <Columns width={820}>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                {slidesCommonContent.one}
                            </ColumnsItem>
                        </Columns>
                    )}
                </FullPageSlide>
                <FullPageSlide>
                    {isBreakpointMobileDevice ? (
                        <Columns width="80%">
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox align={CtaBoxAlign.CENTERED}>
                                    <CtaBoxMedia marginTop={84}>
                                        {mobileMediaBoxPlaceholder}
                                    </CtaBoxMedia>
                                    <CtaBoxTitle marginTop={44}>
                                        <AnimationAppear y={80}>{slides.two.title}</AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={90}>
                                            {slides.two.subTitle}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                </CtaBox>
                            </ColumnsItem>
                        </Columns>
                    ) : (
                        <Columns width={sliderColumnsWidth}>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox>
                                    <CtaBoxMedia>
                                        <AnimationAppear y={50}>
                                            <Image
                                                className="cta-box__media-img"
                                                alt=""
                                                src={Assets.NIGHT_IN_ICON_TRIVIA_ONE}
                                                width={140}
                                                height={140}
                                            />
                                        </AnimationAppear>
                                    </CtaBoxMedia>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={80}>{slides.two.title}</AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={90}>
                                            {slides.two.subTitle}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                </CtaBox>
                            </ColumnsItem>
                            <ColumnsItem size={ColumnsItem.size['1/1']}></ColumnsItem>
                        </Columns>
                    )}
                </FullPageSlide>
                <FullPageSlide extraClass="night-in">
                    {isBreakpointMobileDevice ? (
                        <Columns width="90%">
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox align={CtaBoxAlign.CENTERED}>
                                    <CtaBoxMedia marginTop={84}>
                                        <>{mobileMediaBoxPlaceholder}</>
                                    </CtaBoxMedia>
                                    <CtaBoxTitle marginTop={34}>
                                        <AnimationAppear y={80}>
                                            {slides.three.title}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={90}>
                                            {slides.three.subTitle}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                </CtaBox>
                            </ColumnsItem>
                        </Columns>
                    ) : (
                        <Columns width={996}>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox>
                                    <CtaBoxMedia>
                                        <AnimationAppear y={50}>
                                            <Image
                                                className="cta-box__media-img"
                                                alt=""
                                                src={Assets.NIGHT_IN_ICON_TRIVIA_TWO}
                                                width={140}
                                                height={0.84 * 140}
                                            />
                                        </AnimationAppear>
                                    </CtaBoxMedia>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={80}>
                                            {slides.three.title}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={90}>
                                            {slides.three.subTitle}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                </CtaBox>
                            </ColumnsItem>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                {/* Mock for video */}
                            </ColumnsItem>
                        </Columns>
                    )}
                </FullPageSlide>
                <FullPageSlide>
                    {isBreakpointMobileDevice ? (
                        <Columns width="80%">
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox align={CtaBoxAlign.CENTERED}>
                                    <CtaBoxMedia marginTop={84}>
                                        {mobileMediaBoxPlaceholder}
                                    </CtaBoxMedia>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={80}>
                                            {slides.four.title}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={90}>
                                            {slides.four.subTitle}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                </CtaBox>
                            </ColumnsItem>
                        </Columns>
                    ) : (
                        <Columns width={996}>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox>
                                    <CtaBoxMedia>
                                        <AnimationAppear y={50}>
                                            <Image
                                                className="cta-box__media-img"
                                                alt=""
                                                src={Assets.NIGHT_IN_ICON_TRIVIA_THREE}
                                                width={140}
                                                height={0.85 * 140}
                                            />
                                        </AnimationAppear>
                                    </CtaBoxMedia>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={80}>
                                            {slides.four.title}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={90}>
                                            {slides.four.subTitle}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                </CtaBox>
                            </ColumnsItem>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                {/* Mock for video */}
                            </ColumnsItem>
                        </Columns>
                    )}
                </FullPageSlide>
                <FullPageSlide extraClass="night-in">
                    {isBreakpointMobileDevice ? (
                        <Columns width="100%" fullHeight={true}>
                            <ColumnsItem
                                size={ColumnsItem.size['1/1']}
                                extraClass="columns__item--full-height"
                            >
                                <div className="column__item-row">
                                    <CtaBox align={CtaBoxAlign.CENTERED}>
                                        <CtaBoxTitle marginTop={16}>
                                            <AnimationAppear y={80}>
                                                {slides.five.title}
                                            </AnimationAppear>
                                        </CtaBoxTitle>
                                        <CtaBoxTitle marginTop={48}>
                                            <AnimationAppear y={90}>
                                                {slides.five.cta}
                                            </AnimationAppear>
                                        </CtaBoxTitle>
                                        <>
                                            {showFaqLink ? (
                                                <CtaBoxTitle
                                                    align={CtaBoxTitleAlign.CENTERED}
                                                    marginTop={24}
                                                >
                                                    <AnimationAppear y={90}>
                                                        {slides.five.ctaFAQ}
                                                    </AnimationAppear>
                                                </CtaBoxTitle>
                                            ) : null}
                                        </>
                                    </CtaBox>
                                </div>
                                {isBreakpointsmallTabletMobileDevice ? (
                                    <div className="column__item-row">
                                        {isBreakpointMobileDevice && slide === 4 ? (
                                            <PageFooter
                                                color={color}
                                                goToTopHandler={goToTopHandler}
                                                showGoToButton={true}
                                            />
                                        ) : null}
                                    </div>
                                ) : null}
                            </ColumnsItem>
                        </Columns>
                    ) : (
                        <Columns width={820}>
                            <ColumnsItem size={ColumnsItem.size['1/1']}>
                                <CtaBox align={CtaBoxAlign.CENTERED}>
                                    <CtaBoxTitle marginTop={16}>
                                        <AnimationAppear y={80}>
                                            {slides.five.title}
                                        </AnimationAppear>
                                    </CtaBoxTitle>
                                    <CtaBoxTitle marginTop={48}>
                                        <AnimationAppear y={90}>{slides.five.cta}</AnimationAppear>
                                    </CtaBoxTitle>
                                    <>
                                        {showFaqLink ? (
                                            <CtaBoxTitle
                                                align={CtaBoxTitleAlign.CENTERED}
                                                marginTop={24}
                                            >
                                                <AnimationAppear y={90}>
                                                    {slides.five.ctaFAQ}
                                                </AnimationAppear>
                                            </CtaBoxTitle>
                                        ) : null}
                                    </>
                                </CtaBox>
                            </ColumnsItem>
                        </Columns>
                    )}
                </FullPageSlide>
            </FullPageSlider>
            {isBreakpointMobileDevice ? floatingVideoMW : floatingVideoDW}
        </>
    );
};

export default PageSlider;
